import { useState, useEffect, useCallback, useMemo } from 'react';
import { Batiment } from '../../../../shared/types/game';
import config from '../../../../config/index';

const BASE_CAPACITY_PER_LEVEL = config.buildings.capacity.baseCapacityPerLevel;
const CAPACITY_INCREASE_PER_LEVEL = config.buildings.capacity.capacityIncreasePerLevel;
const CAPACITY_PER_AUGMENTATION = config.buildings.capacity.capacityPerAugmentation;
const TEMPS_PAR_PLACE_SECONDES = config.buildings.augmentation.timePerPlaceSeconds;

export const useBuildingCapacity = (
  buildingData: Batiment,
  setBuildingData: React.Dispatch<React.SetStateAction<Batiment>>,
  onBuildingUpdate?: (updatedBuilding: Batiment) => void
) => {
  const XP_REQUIS_PAR_NIVEAU = buildingData.xp_requis_par_niveau || config.buildings.experience.xpRequiredPerLevel;

  // Calculer le niveau basé sur l'XP
  const calculateLevelFromXP = useCallback((xp: number = 0): number => {
    return 1 + Math.floor(xp / XP_REQUIS_PAR_NIVEAU);
  }, [XP_REQUIS_PAR_NIVEAU]);

  // Calculer la capacité totale pour le personnel
  const calculateTotalCapacity = useCallback((niveau: number, augmentations: number = 0): number => {
    const baseCapacity = BASE_CAPACITY_PER_LEVEL + (niveau - 1) * CAPACITY_INCREASE_PER_LEVEL;
    const augmentationCapacity = (augmentations || 0) * CAPACITY_PER_AUGMENTATION;
    return baseCapacity + augmentationCapacity;
  }, []);

  // Calculer la capacité totale pour les véhicules
  const calculateVehiclesCapacity = useCallback((niveau: number, augmentations: number = 0): number => {
    const baseCapacity = BASE_CAPACITY_PER_LEVEL + (niveau - 1) * CAPACITY_INCREASE_PER_LEVEL;
    const augmentationCapacity = (augmentations || 0) * CAPACITY_PER_AUGMENTATION;
    return baseCapacity + augmentationCapacity;
  }, []);

  // Calculer le niveau actuel basé sur l'XP
  const currentXP = buildingData.xp || 0;
  const calculatedLevel = useMemo(() => calculateLevelFromXP(currentXP), [currentXP, calculateLevelFromXP]);
  const totalCapacity = useMemo(() => {
    return calculateTotalCapacity(calculatedLevel, buildingData.augmentations_capacite_personnel || 0);
  }, [calculatedLevel, buildingData.augmentations_capacite_personnel, calculateTotalCapacity]);
  const totalVehiclesCapacity = useMemo(() => {
    return calculateVehiclesCapacity(calculatedLevel, buildingData.augmentations_capacite_vehicules || 0);
  }, [calculatedLevel, buildingData.augmentations_capacite_vehicules, calculateVehiclesCapacity]);

  // Mettre à jour le niveau et la capacité quand l'XP ou les augmentations changent
  useEffect(() => {
    const currentOccupied = buildingData.postes_occupes || 0;
    const newAvailable = Math.max(0, totalCapacity - currentOccupied);
    
    if (buildingData.niveau !== calculatedLevel || buildingData.capacite !== totalCapacity || buildingData.capacite_vehicules !== totalVehiclesCapacity) {
      setBuildingData(prev => ({
        ...prev,
        niveau: calculatedLevel,
        capacite: totalCapacity,
        capacite_vehicules: totalVehiclesCapacity,
        postes_disponibles: newAvailable
      }));
    }
  }, [calculatedLevel, totalCapacity, totalVehiclesCapacity, buildingData.postes_occupes, buildingData.niveau, buildingData.capacite, buildingData.capacite_vehicules, setBuildingData]);

  // État pour les timers d'augmentation
  const [timeRemainingPersonnel, setTimeRemainingPersonnel] = useState<number | null>(null);
  const [timeRemainingVehicules, setTimeRemainingVehicules] = useState<number | null>(null);

  // Timer pour afficher le temps restant pour le personnel
  useEffect(() => {
    if (buildingData.augmentation_personnel_en_cours) {
      const updateTimer = () => {
        const now = new Date().getTime();
        const dateFin = new Date(buildingData.augmentation_personnel_en_cours!.date_fin).getTime();
        const remaining = Math.max(0, Math.floor((dateFin - now) / 1000));
        setTimeRemainingPersonnel(remaining);
      };

      updateTimer();
      const interval = setInterval(updateTimer, 1000);

      return () => clearInterval(interval);
    } else {
      setTimeRemainingPersonnel(null);
    }
  }, [buildingData.augmentation_personnel_en_cours]);

  // Timer pour afficher le temps restant pour les véhicules
  useEffect(() => {
    if (buildingData.augmentation_vehicules_en_cours) {
      const updateTimer = () => {
        const now = new Date().getTime();
        const dateFin = new Date(buildingData.augmentation_vehicules_en_cours!.date_fin).getTime();
        const remaining = Math.max(0, Math.floor((dateFin - now) / 1000));
        setTimeRemainingVehicules(remaining);
      };

      updateTimer();
      const interval = setInterval(updateTimer, 1000);

      return () => clearInterval(interval);
    } else {
      setTimeRemainingVehicules(null);
    }
  }, [buildingData.augmentation_vehicules_en_cours]);

  // Formatage du temps restant
  const formatTimeRemaining = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  // Fonction pour démarrer une augmentation de capacité
  const handleStartAugmentation = (type: 'personnel' | 'vehicules', places: number = 1) => {
    if (type === 'personnel' && buildingData.augmentation_personnel_en_cours) {
      alert('Une augmentation de personnel est déjà en cours. Veuillez attendre qu\'elle se termine.');
      return;
    }
    if (type === 'vehicules' && buildingData.augmentation_vehicules_en_cours) {
      alert('Une augmentation de véhicules est déjà en cours. Veuillez attendre qu\'elle se termine.');
      return;
    }

    const dureeSecondes = places * TEMPS_PAR_PLACE_SECONDES;
    const dateDebut = new Date();
    const dateFin = new Date(dateDebut.getTime() + dureeSecondes * 1000);

    const augmentationData = {
      places,
      date_debut: dateDebut.toISOString(),
      duree_secondes: dureeSecondes,
      date_fin: dateFin.toISOString()
    };

    const updated = {
      ...buildingData,
      updated_at: new Date().toISOString()
    };

    if (type === 'personnel') {
      updated.augmentation_personnel_en_cours = augmentationData;
    } else {
      updated.augmentation_vehicules_en_cours = augmentationData;
    }
    
    setBuildingData(updated);
    if (onBuildingUpdate) {
      onBuildingUpdate(updated);
    }
  };

  const completeAugmentation = useCallback(
    (type: 'personnel' | 'vehicules') => {
      const currentAugmentation =
        type === 'personnel'
          ? buildingData.augmentation_personnel_en_cours
          : buildingData.augmentation_vehicules_en_cours;
      if (!currentAugmentation) return;

      const gainedSteps = Math.max(1, Number(currentAugmentation.places) || 1);
      const updated: Batiment = {
        ...buildingData,
        augmentation_personnel_en_cours:
          type === 'personnel' ? null : buildingData.augmentation_personnel_en_cours,
        augmentation_vehicules_en_cours:
          type === 'vehicules' ? null : buildingData.augmentation_vehicules_en_cours,
        augmentations_capacite_personnel:
          type === 'personnel'
            ? (buildingData.augmentations_capacite_personnel || 0) + gainedSteps
            : buildingData.augmentations_capacite_personnel || 0,
        augmentations_capacite_vehicules:
          type === 'vehicules'
            ? (buildingData.augmentations_capacite_vehicules || 0) + gainedSteps
            : buildingData.augmentations_capacite_vehicules || 0,
        updated_at: new Date().toISOString(),
      };

      setBuildingData(updated);
      if (onBuildingUpdate) {
        onBuildingUpdate(updated);
      }
    },
    [buildingData, onBuildingUpdate, setBuildingData]
  );

  // Finaliser automatiquement l'augmentation personnel quand le compteur arrive à zéro
  useEffect(() => {
    const aug = buildingData.augmentation_personnel_en_cours;
    if (!aug?.date_fin) return;
    const due = new Date(aug.date_fin).getTime() <= Date.now();
    if (due) {
      completeAugmentation('personnel');
    }
  }, [buildingData.augmentation_personnel_en_cours, completeAugmentation]);

  // Finaliser automatiquement l'augmentation véhicules quand le compteur arrive à zéro
  useEffect(() => {
    const aug = buildingData.augmentation_vehicules_en_cours;
    if (!aug?.date_fin) return;
    const due = new Date(aug.date_fin).getTime() <= Date.now();
    if (due) {
      completeAugmentation('vehicules');
    }
  }, [buildingData.augmentation_vehicules_en_cours, completeAugmentation]);

  return {
    calculatedLevel,
    totalCapacity,
    totalVehiclesCapacity,
    timeRemainingPersonnel,
    timeRemainingVehicules,
    handleStartAugmentation,
    formatTimeRemaining,
    BASE_CAPACITY_PER_LEVEL,
    CAPACITY_INCREASE_PER_LEVEL,
    CAPACITY_PER_AUGMENTATION,
    XP_REQUIS_PAR_NIVEAU
  };
};

